/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import "./addProject.css";
import { UilCheck, UilImageUpload, UilPlus } from "@iconscout/react-unicons";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { Tooltip } from "react-tooltip";

const AddProject = () => {
  const [name, setName] = useState("");
  const [arName, setArName] = useState("");
  const [category, setCategory] = useState("");
  const [arCategory, setArCategory] = useState("");
  const [description, setDescription] = useState("");
  const [arDescription, setArDescription] = useState("");
  const [image, setImage] = useState(null);
  const [secondaryImages, setSecondaryImages] = useState([]);
  const [alt, setAlt] = useState("");
  const [arAlt, setArAlt] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [pickedExcelName, setPickedExcelName] = useState("");
  const [pickedZipName, setPickedZipName] = useState("");
  /** Last successful / failed validation from POST /projects/bulk/validate */
  const [bulkValidation, setBulkValidation] = useState(null);
  const excelInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setError("");
    } else {
      setError("Please upload a valid image file (png, jpg, jpeg).");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleRemoveImage = () => {
    setImage(null);
  };

  const handleSecondryImagesUpload = (file, index) => {
    const newSecondaryImages = [...secondaryImages];
    newSecondaryImages[index] = file;
    setSecondaryImages(newSecondaryImages);
  };

  const handleRemoveSecondryImages = (index) => {
    const newSecondaryImages = [...secondaryImages];
    newSecondaryImages.splice(index, 1);
    setSecondaryImages(newSecondaryImages);
  };

  const handleAddSecondaryImage = () => {
    setSecondaryImages([...secondaryImages, null]);
  };

  const imageFormats = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "webp",
    "svg",
    "heic",
    "ico",
    "avif",
  ];

  const handleAddProject = async (e) => {
    e.preventDefault();

    if (
      !name ||
      !arName ||
      !category ||
      !arCategory ||
      !description ||
      !arDescription ||
      !image ||
      !alt ||
      !arAlt
    ) {
      return toast.error("Please fill out all fields.");
    }

    secondaryImages.forEach((img) => {
      if (img && !img.type.startsWith("image/")) {
        return toast.error("All secondary images must be valid image files.");
      }
    });

    const projectData = new FormData();
    projectData.append("name", name);
    projectData.append("arName", arName);
    projectData.append("category", category);
    projectData.append("arCategory", arCategory);
    projectData.append("description", description);
    projectData.append("arDescription", arDescription);
    projectData.append("image", image);
    projectData.append("alt", alt);
    projectData.append("arAlt", arAlt);

    secondaryImages.forEach((img) => {
      if (img) {
        projectData.append("secondaryImages", img);
      }
    });

    try {
      setLoading(true);
      const response = await toast.promise(
        axios.post("/projects", projectData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Adding project...",
          success: "Project added successfully!",
        }
      );

      setName("");
      setArName("");
      setCategory("");
      setArCategory("");
      setDescription("");
      setArDescription("");
      setImage(null);
      setAlt("");
      setArAlt("");
      setSecondaryImages([]);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setTemplateLoading(true);
      const res = await axios.get("/projects/bulk/template", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      downloadBlob(res.data, "projects-import-template.xlsx");
      toast.success("Template downloaded.");
    } catch (err) {
      console.error(err);
      toast.error("Could not download template.");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleExcelPicked = (e) => {
    const f = e.target.files?.[0];
    setPickedExcelName(f ? f.name : "");
    setBulkValidation(null);
  };

  const handleZipPicked = (e) => {
    const f = e.target.files?.[0];
    setPickedZipName(f ? f.name : "");
    setBulkValidation(null);
  };

  const buildBulkFormData = () => {
    const excelFile = excelInputRef.current?.files?.[0];
    if (!excelFile) return null;
    const zipFile = zipInputRef.current?.files?.[0];
    const formData = new FormData();
    formData.append("file", excelFile);
    if (zipFile) formData.append("imagesZip", zipFile);
    return formData;
  };

  const handleCheckRows = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    const formData = buildBulkFormData();
    if (!formData) {
      toast.warn("Choose an Excel file (.xlsx) first.");
      return;
    }
    try {
      setValidateLoading(true);
      const { data } = await axios.post("/projects/bulk/validate", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBulkValidation(data);
      if (data.allValid) {
        toast.success(data.message || "All rows look good.");
      } else {
        toast.warning(data.message || "Some rows need fixes.");
      }
    } catch (err) {
      console.error(err);
      setBulkValidation(null);
      toast.error(
        err.response?.data?.message || "Could not validate. Check the file and ZIP."
      );
    } finally {
      setValidateLoading(false);
    }
  };

  const handleRunImport = async () => {
    if (!bulkValidation?.allValid) {
      toast.warn('Click "Check rows" first and fix any errors until all rows pass.');
      return;
    }
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    const formData = buildBulkFormData();
    if (!formData) {
      toast.warn("Choose an Excel file (.xlsx).");
      return;
    }

    try {
      setImportLoading(true);
      const { data } = await axios.post("/projects/bulk/import", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(data.message || "Import completed.");
      if (excelInputRef.current) excelInputRef.current.value = "";
      if (zipInputRef.current) zipInputRef.current.value = "";
      setPickedExcelName("");
      setPickedZipName("");
      setBulkValidation(null);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Import failed.";
      const errs = err.response?.data?.errors;
      if (Array.isArray(errs) && errs.length) {
        const preview = errs
          .slice(0, 4)
          .map((e) => `Row ${e.row}: ${e.message}`)
          .join(" · ");
        toast.error(`${msg} ${preview}${errs.length > 4 ? " · …" : ""}`, {
          autoClose: 12000,
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setImportLoading(false);
    }
  };

  const bulkBusy = templateLoading || importLoading || validateLoading;

  return (
    <>
      <main className="addProject">
        <form className="container" onSubmit={handleAddProject}>
          <div className="title">
            <div className="text">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="35"
                viewBox="0 0 34 35"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.3511 2.271C9.21636 2.271 8.43861 2.271 7.74444 2.45941C7.02601 2.65659 6.35707 3.00266 5.78108 3.47517C5.20509 3.94768 4.73492 4.53605 4.40111 5.20208C4.07952 5.84241 3.92794 6.606 3.70552 7.7195L2.82719 12.1083C2.67164 12.8777 2.68667 13.6718 2.87122 14.4347C3.05577 15.1977 3.40536 15.9109 3.89536 16.5241V20.4128C3.89536 23.0167 3.89536 25.0793 4.11211 26.6929C4.33594 28.3532 4.80627 29.6977 5.86594 30.7587C6.92561 31.8184 8.27003 32.2887 9.93178 32.5126C11.5454 32.7293 13.608 32.7293 16.2104 32.7293H17.7872C20.391 32.7293 22.4537 32.7293 24.0673 32.5126C25.7276 32.2887 27.072 31.8184 28.1331 30.7587C29.1928 29.6977 29.6631 28.3532 29.8869 26.6929C30.1037 25.0779 30.1037 23.0167 30.1037 20.4128V16.5255C30.5937 15.912 30.9431 15.1985 31.1274 14.4353C31.3117 13.6721 31.3264 12.8778 31.1704 12.1083L30.2921 7.7195C30.0711 6.606 29.9181 5.84241 29.5979 5.20208C29.264 4.5359 28.7936 3.94744 28.2173 3.47492C27.6411 3.00241 26.9719 2.65641 26.2532 2.45941C25.5604 2.271 24.7827 2.271 23.6465 2.271H10.3511ZM25.8792 18.5627C26.6002 18.564 27.3142 18.4204 27.9787 18.1405V20.3335C27.9787 23.0351 27.9759 24.9547 27.7804 26.411C27.5891 27.8347 27.2293 28.6564 26.63 29.2557C26.0308 29.8549 25.2091 30.2147 23.7825 30.406C23.0593 30.4948 22.3321 30.5487 21.6037 30.5675V26.6632C21.6037 26.0398 21.6037 25.5015 21.564 25.0623C21.5215 24.5948 21.428 24.133 21.1759 23.6981C20.8964 23.2134 20.4943 22.8108 20.0099 22.5307C19.575 22.28 19.1132 22.1865 18.6457 22.144C18.2065 22.1043 17.6682 22.1043 17.0449 22.1043H16.9528C16.3294 22.1043 15.7925 22.1043 15.3519 22.144C14.8844 22.1865 14.424 22.28 13.9891 22.5307C13.5042 22.8106 13.1016 23.2132 12.8218 23.6981C12.571 24.133 12.4775 24.5948 12.435 25.0609C12.3954 25.5029 12.3954 26.0398 12.3954 26.6617V30.5675C11.666 30.5488 10.9379 30.4949 10.2137 30.406C8.78994 30.2147 7.96828 29.8549 7.36903 29.2557C6.76977 28.6564 6.40994 27.8347 6.21869 26.4096C6.02177 24.9547 6.02036 23.0351 6.02036 20.3335V18.1405C7.15254 18.618 8.41376 18.6949 9.59557 18.3585C10.7774 18.0221 11.809 17.2925 12.52 16.2903C13.034 16.9961 13.708 17.57 14.4866 17.965C15.2653 18.36 16.1264 18.5648 16.9995 18.5627C17.8726 18.5648 18.7338 18.36 19.5124 17.965C20.2911 17.57 20.965 16.9961 21.479 16.2903C21.977 16.9936 22.6366 17.5671 23.4023 17.9625C24.168 18.3579 25.0174 18.5637 25.8792 18.5627ZM14.5204 30.6015C15.07 30.6034 15.6603 30.6043 16.2912 30.6043H17.7079C18.3387 30.6043 18.929 30.6034 19.4787 30.6015V26.7085C19.4787 26.0271 19.4773 25.5865 19.4475 25.2536C19.4192 24.9348 19.3696 24.8187 19.3356 24.7606C19.2425 24.5994 19.1086 24.4656 18.9474 24.3724C18.8894 24.3384 18.7732 24.2902 18.4544 24.2605C17.9701 24.2309 17.4847 24.2205 16.9995 24.2293C16.3181 24.2293 15.8775 24.2293 15.5446 24.2605C15.2244 24.2888 15.1097 24.3384 15.0516 24.3724C14.8899 24.4654 14.7556 24.5992 14.662 24.7606C14.6294 24.8172 14.5799 24.9348 14.5515 25.2536C14.5218 25.5865 14.5204 26.0271 14.5204 26.7085V30.6015ZM12.282 4.396H10.5084C9.15686 4.396 8.68794 4.40591 8.30827 4.50933C7.87729 4.62754 7.47598 4.83505 7.13039 5.11841C6.7848 5.40176 6.50266 5.75462 6.30227 6.15408C6.12519 6.50683 6.02461 6.963 5.75969 8.289L4.91252 12.5248C4.82224 12.9607 4.8218 13.4103 4.91125 13.8463C5.00069 14.2823 5.17814 14.6955 5.43273 15.0606C5.68732 15.4257 6.01371 15.735 6.39193 15.9696C6.77016 16.2042 7.19226 16.3592 7.63244 16.4251C8.07262 16.491 8.52161 16.4664 8.95197 16.3529C9.38232 16.2393 9.78498 16.0391 10.1353 15.7646C10.4857 15.4901 10.7763 15.147 10.9896 14.7563C11.2028 14.3657 11.334 13.9356 11.3754 13.4924L11.4731 12.5206L11.4788 12.4611L12.282 4.396ZM13.5868 12.7189L14.4198 4.396H19.5793L20.4066 12.6722C20.453 13.1457 20.4001 13.6237 20.2515 14.0757C20.1028 14.5277 19.8615 14.9438 19.5431 15.2973C19.2247 15.6509 18.836 15.9342 18.402 16.1292C17.968 16.3242 17.4981 16.4265 17.0223 16.4298C16.5465 16.4331 16.0752 16.3371 15.6386 16.1481C15.2019 15.9591 14.8094 15.6811 14.4862 15.332C14.1629 14.9828 13.916 14.5701 13.7612 14.1202C13.6063 13.6703 13.5469 13.1931 13.5868 12.7189ZM25.6908 4.50933C25.3111 4.40591 24.8422 4.396 23.4907 4.396H21.7142L22.6251 13.491C22.6678 13.9329 22.7999 14.3615 23.0135 14.7506C23.2271 15.1398 23.5177 15.4814 23.8676 15.7547C24.2174 16.028 24.6193 16.2271 25.0486 16.3401C25.4779 16.4531 25.9257 16.4775 26.3648 16.4119C26.8039 16.3462 27.225 16.1919 27.6025 15.9582C27.98 15.7246 28.306 15.4166 28.5606 15.053C28.8152 14.6893 28.9932 14.2776 29.0836 13.843C29.174 13.4084 29.175 12.9599 29.0865 12.5248L28.2408 8.289C27.9744 6.963 27.8739 6.50683 27.6982 6.15408C27.4977 5.75447 27.2153 5.40152 26.8695 5.11816C26.5236 4.8348 26.122 4.62736 25.6908 4.50933Z"
                  fill="black"
                  fillOpacity={0.6}
                />
              </svg>
              <p>Add New Project</p>
            </div>
            <button>
              <UilCheck size={22} />
              Add Project
            </button>
          </div>
          <div className="form">
            <div className="inputs">
              <h3>General information</h3>
              <div className="input">
                <label>
                  Name <span>*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  placeholder="Give your project a short and clear name."
                />
              </div>
              <div className="input ar-input">
                <label>
                  الاسم <span>*</span>
                </label>
                <input
                  value={arName}
                  onChange={(e) => setArName(e.target.value)}
                  type="text"
                  placeholder="امنح مشروعك اسمًا قصيرًا وواضحًا."
                />
              </div>
              <hr />
              <div className="input">
                <label>
                  Category <span>*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="" hidden>
                    Choose your project category.
                  </option>
                  <option value="" disabled>
                    Categories
                  </option>
                  <option value="Work Space">Work Space</option>
                  <option value="Education">Education</option>
                  <option value="Residential">Residential</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Hospitality">Hospitality</option>
                </select>
              </div>
              <div className="input ar-input">
                <label>
                  الفئة <span>*</span>
                </label>
                <select
                  value={arCategory}
                  onChange={(e) => setArCategory(e.target.value)}
                >
                  <option value="" hidden>
                    اختر فئة مشروعك.
                  </option>
                  <option value="مساحة العمل">مساحة العمل</option>
                  <option value="تعليم">تعليم</option>
                  <option value="سكني">سكني</option>
                  <option value="ترفيه">ترفيه</option>
                  <option value="تجاري">تجاري</option>
                  <option value="ضيافة">ضيافة</option>
                </select>
              </div>
              <hr />
              <div className="input textarea">
                <label>
                  Description <span>*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Give your project a short and clear description."
                ></textarea>
              </div>
              <div className="input textarea ar-input">
                <label>
                  الوصف <span>*</span>
                </label>
                <textarea
                  value={arDescription}
                  onChange={(e) => setArDescription(e.target.value)}
                  placeholder="امنح مشروعك وصفًا قصيرًا وواضحًا."
                ></textarea>
              </div>
            </div>
            <div className="image">
              <h3>Upload image</h3>

              {image ? (
                <div className="imgPreview">
                  <img src={URL.createObjectURL(image)} alt="Uploaded" />
                  <button className="remove-btn" onClick={handleRemoveImage}>
                    <i className="uil uil-times"></i>
                  </button>
                </div>
              ) : (
                <div
                  className="imgInput"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <UilImageUpload size={40} />
                  <p>Drop your main image here, or</p>
                  <div className="btn">
                    <button type="button">Click here</button>
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      accept="image/*"
                    />
                  </div>
                </div>
              )}

              {error && <p className="error">{error}</p>}

              <div className="secondary-images">
                {secondaryImages.map((img, index) => (
                  <div
                    key={index}
                    className="image-container"
                    style={{ width: "100%" }}
                  >
                    {img ? (
                      <div className="imgPreview">
                        <img
                          src={URL.createObjectURL(img)}
                          alt={`Uploaded ${index}`}
                        />
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveSecondryImages(index)}
                        >
                          <i className="uil uil-times"></i>
                        </button>
                      </div>
                    ) : (
                      <div
                        className="imgInput"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleSecondryImagesUpload(
                            e.dataTransfer.files[0],
                            index
                          );
                        }}
                      >
                        <UilImageUpload size={24} />
                        <div className="btn">
                          <button type="button">Click</button>
                          <input
                            type="file"
                            onChange={(e) =>
                              handleSecondryImagesUpload(
                                e.target.files[0],
                                index
                              )
                            }
                            accept="image/*"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {secondaryImages.length < 100 && (
                  <div
                    className="imgInput add-more"
                    onClick={handleAddSecondaryImage}
                  >
                    <UilPlus size={24} />
                    <p>Add more</p>
                  </div>
                )}
              </div>

              {!image && (
                <>
                  <div className="image-p">
                    <p className="inf">Upload your project images.</p>
                    <i className="uil uil-info-circle" id="formats"></i>
                  </div>
                  <Tooltip
                    anchorSelect="#formats"
                    content={imageFormats?.join(", ")}
                    place="top"
                  />
                </>
              )}

              <div className="input">
                <label>
                  Image alt <span>*</span>
                </label>
                <input
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  type="text"
                  placeholder="Give your project image a short alt text."
                />
              </div>
              <div className="input ar-input">
                <label>
                  نص الصورة البديل <span>*</span>
                </label>
                <input
                  value={arAlt}
                  onChange={(e) => setArAlt(e.target.value)}
                  type="text"
                  placeholder="أعط صورة مشروعك نصًا بديلًا قصيرًا."
                />
              </div>
            </div>
          </div>

          <section
            className="add-project-bulk add-project-bulk--page-bottom"
            aria-label="Bulk import projects"
          >
            <div className="add-project-bulk__head">
              <h2 className="add-project-bulk__title">Bulk import</h2>
              <p className="add-project-bulk__subtitle">
                Use the <strong>Projects</strong> sheet. Format <code>_id</code> as{" "}
                <strong>Text</strong> in Excel so IDs are not corrupted. File names
                must match the ZIP (
                <code>image_main_filename</code>,{" "}
                <code>secondary_image_names</code>). Export from{" "}
                <Link to="/dashboard/projects">Projects</Link>.{" "}
                If <code>_id</code> is filled (update),{" "}
                <strong>blank cells keep the current value</strong> — only filled
                cells are changed. For <strong>new</strong> rows, leave{" "}
                <code>_id</code> empty and fill <code>name</code> and{" "}
                <code>image_main_filename</code>.{" "}
                <strong>Check rows</strong> before import — nothing is saved until
                every row passes. Max upload size: <strong>25 MB</strong> Excel,{" "}
                <strong>1 GB</strong> ZIP.
              </p>
            </div>
            <div className="add-project-bulk__actions">
              <button
                type="button"
                className="add-project-bulk__btn add-project-bulk__btn--secondary"
                disabled={bulkBusy}
                onClick={handleDownloadTemplate}
              >
                {templateLoading ? "…" : "Download import template"}
              </button>
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="add-project-bulk__file-input"
                onChange={handleExcelPicked}
                disabled={importLoading || validateLoading}
              />
              <button
                type="button"
                className="add-project-bulk__btn add-project-bulk__btn--ghost"
                disabled={importLoading || validateLoading}
                onClick={() => excelInputRef.current?.click()}
              >
                {pickedExcelName || "Choose Excel…"}
              </button>
              <input
                ref={zipInputRef}
                type="file"
                accept=".zip,application/zip"
                className="add-project-bulk__file-input"
                onChange={handleZipPicked}
                disabled={importLoading || validateLoading}
              />
              <button
                type="button"
                className="add-project-bulk__btn add-project-bulk__btn--ghost"
                disabled={importLoading || validateLoading}
                onClick={() => zipInputRef.current?.click()}
              >
                {pickedZipName || "ZIP (optional)"}
              </button>
              <button
                type="button"
                className="add-project-bulk__btn add-project-bulk__btn--secondary"
                disabled={bulkBusy || !pickedExcelName}
                onClick={handleCheckRows}
              >
                {validateLoading ? "Checking…" : "Check rows"}
              </button>
              <button
                type="button"
                className="add-project-bulk__btn add-project-bulk__btn--accent"
                disabled={
                  importLoading ||
                  validateLoading ||
                  !pickedExcelName ||
                  !bulkValidation?.allValid
                }
                onClick={handleRunImport}
              >
                {importLoading ? "Importing…" : "Import to database"}
              </button>
            </div>
            {bulkValidation && (
              <div
                className={`add-project-bulk__report ${
                  bulkValidation.allValid
                    ? "add-project-bulk__report--ok"
                    : "add-project-bulk__report--issues"
                }`}
                role="status"
              >
                <p className="add-project-bulk__report-summary">
                  {bulkValidation.message}
                </p>
                {Array.isArray(bulkValidation.rows) &&
                  bulkValidation.rows.length > 0 && (
                    <ul className="add-project-bulk__report-list">
                      {bulkValidation.rows.map((r) => (
                        <li
                          key={r.row}
                          className={
                            r.valid
                              ? "add-project-bulk__report-item add-project-bulk__report-item--ok"
                              : "add-project-bulk__report-item add-project-bulk__report-item--bad"
                          }
                        >
                          <span className="add-project-bulk__report-row">
                            Row {r.row}
                          </span>
                          <span className="add-project-bulk__report-action">
                            {r.action}
                          </span>
                          {r.valid ? (
                            <span className="add-project-bulk__report-msg">OK</span>
                          ) : (
                            <ul className="add-project-bulk__report-errors">
                              {r.errors.map((msg, i) => (
                                <li key={i}>{msg}</li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            )}
          </section>
        </form>
      </main>
    </>
  );
};

export default AddProject;
